import { useState } from 'react';
import { Shield, ShieldOff, Settings, FileText, Lock, Unlock, AlertCircle } from 'lucide-react';
import { ContentProtectionProvider, ProtectionToast, AllowListConfig } from '@/components/ContentProtection';

const DEMO_CONTENT = {
  title: '知识产权保护声明',
  body: `本文档包含受版权保护的商业机密和专有信息。未经授权，严禁复制、传播或以任何方式使用本文档内容。

根据《中华人民共和国著作权法》和相关国际条约，本文档及其所有内容均受法律保护。任何侵权行为将追究法律责任。`,
  secret: `核心算法参数:
- 加密密钥: AES-256-GCM
- 签名算法: ECDSA P-256
- 哈希函数: SHA-3 256
- 协议版本: v2.1.0-stable

注意：以上信息为商业机密，泄露将造成重大经济损失。`,
};

const ContentProtectionDemo = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [allowList, setAllowList] = useState<string[]>(['.allow-copy']);

  return (
    <ContentProtectionProvider enabled={isEnabled} allowList={allowList}>
      <div className="min-h-screen bg-gray-50">
        <ProtectionToast position="top-center" duration={2500} />

        {/* Header */}
        <header className="bg-[#333333] text-white">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8" />
                <div>
                  <h1 className="text-xl font-bold">内容保护工具</h1>
                  <p className="text-sm text-gray-400">防止关键信息被复制，保护知识产权</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsEnabled(!isEnabled)}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                    isEnabled
                      ? 'bg-[#e74c3c] hover:bg-red-600'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {isEnabled ? (
                    <>
                      <Lock className="w-4 h-4" />
                      保护中
                    </>
                  ) : (
                    <>
                      <Unlock className="w-4 h-4" />
                      已关闭
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                    showConfig
                      ? 'bg-[#f39c12] text-white'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  配置
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Status Banner */}
          <div
            className={`mb-6 p-4 rounded border-l-4 ${
              isEnabled
                ? 'bg-red-50 border-[#e74c3c]'
                : 'bg-green-50 border-green-500'
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  isEnabled ? 'text-[#e74c3c]' : 'text-green-600'
                }`}
              />
              <div>
                <h3 className="font-medium text-[#2c3e50]">
                  {isEnabled ? '内容保护已启用' : '内容保护已关闭'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {isEnabled
                    ? '右键菜单、复制、剪切、全选等功能已被禁用。尝试这些操作将看到提示信息。'
                    : '所有保护功能已暂停，用户可以自由复制页面内容。'}
                </p>
              </div>
            </div>
          </div>

          {/* Config Panel */}
          {showConfig && (
            <div className="mb-6">
              <AllowListConfig allowList={allowList} onChange={setAllowList} />
            </div>
          )}

          {/* Demo Content - Protected */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#e74c3c]" />
                <span className="text-sm font-medium text-[#2c3e50]">
                  受保护区域（无法复制）
                </span>
              </div>
            </div>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-[#2c3e50] mb-4">
                {DEMO_CONTENT.title}
              </h2>
              <p className="text-[#2c3e50] leading-relaxed whitespace-pre-line mb-6">
                {DEMO_CONTENT.body}
              </p>
              <div className="bg-gray-100 rounded p-4 font-mono text-sm text-[#333333]">
                <pre className="whitespace-pre-wrap">{DEMO_CONTENT.secret}</pre>
              </div>
            </div>
          </div>

          {/* Demo Content - Allow Copy */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <div className="flex items-center gap-2">
                <Unlock className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  白名单区域（允许复制）
                </span>
              </div>
            </div>
            <div className="p-6 allow-copy">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900">联系我们</h3>
                    <p className="text-sm text-blue-800 mt-1">
                      如需获取授权或了解更多信息，请联系我们的法务部门。
                    </p>
                    <div className="mt-3 text-sm text-blue-700">
                      <p>邮箱: legal@example.com</p>
                      <p>电话: 400-123-4567</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gray-100 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-[#2c3e50] mb-4">功能说明</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#333333] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#2c3e50]">内容保护</h4>
                  <p className="text-sm text-gray-600">
                    禁止右键菜单、复制、剪切、全选、拖拽等操作
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldOff className="w-5 h-5 text-[#e74c3c] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#2c3e50]">视觉提示</h4>
                  <p className="text-sm text-gray-600">
                    触发保护时显示友好提示，说明内容保护规则
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Settings className="w-5 h-5 text-[#f39c12] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#2c3e50]">白名单配置</h4>
                  <p className="text-sm text-gray-600">
                    支持配置特定区域允许复制，平衡保护和体验
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#2c3e50]">移动端适配</h4>
                  <p className="text-sm text-gray-600">
                    禁止长按复制和选择文本功能
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              免责声明：本工具提供的内容保护仅作为威慑和提示作用，无法完全阻止有心用户的复制行为。
              如需更高级别的保护，请考虑服务端渲染、水印、DRM 等技术方案。
            </p>
          </div>
        </div>
      </div>
    </ContentProtectionProvider>
  );
};

export default ContentProtectionDemo;
